/**
 * AccessDeniedPage Component
 *
 * Shown when a user tries to access a route they don't have permission for.
 * Three variants based on the reason:
 * - "unconfigured" — route has no permissions mapping (dev error)
 * - "no-permission" — user lacks the required permission (role-based)
 * - default — generic access denied
 */

import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { LockClosedIcon, ArrowLeftIcon, HomeIcon } from '@heroicons/react/24/outline'
import { useAuth } from '@/shared/contexts/AuthContext'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api-client'
import { MODULES_CONFIG } from '@/config/modules-config'

interface AccessDeniedPageProps {
  module?: string
  action?: string
  /** Custom message to show instead of the default */
  message?: string
}

type BackendPermissions = Record<string, Record<string, boolean>>

/** Find first accessible href by scanning MODULES_CONFIG (same logic as SmartRedirect) */
function findFirstAccessibleHref(permissions: BackendPermissions | undefined): string | null {
  if (!permissions) return null

  for (const module of MODULES_CONFIG) {
    if (module.requiredPermission) {
      const [mod, action] = module.requiredPermission.split('.')
      if (permissions[mod]?.[action] !== true) continue
    }

    for (const item of module.items) {
      if (item.requiredPermission) {
        const [mod, action] = item.requiredPermission.split('.')
        if (permissions[mod]?.[action] !== true) continue
      }

      if (permissions[module.id]?.[item.id] === true) {
        return item.href
      }
    }
  }

  return null
}

export function AccessDeniedPage({ module, action, message }: AccessDeniedPageProps) {
  const navigate = useNavigate()
  const { user } = useAuth()

  const { data: permissions } = useQuery<BackendPermissions>({
    queryKey: ['permissions', 'me', 'access-denied'],
    queryFn: async () => {
      const data = await apiClient.get<BackendPermissions>('/auth/permissions/me/')
      return data || {}
    },
    staleTime: 60 * 1000,
  })

  const firstAccessibleHref = useMemo(() => {
    return findFirstAccessibleHref(permissions)
  }, [permissions])

  // Determine the variant based on the message content
  const isUnconfigured = message?.includes('no tiene permisos configurados')

  const title = isUnconfigured
    ? 'Seccion no disponible'
    : 'Sin acceso'

  const description = isUnconfigured
    ? 'Esta seccion aun no esta habilitada. Estamos trabajando en ello.'
    : message || (module
      ? `No tienes permisos para acceder al modulo ${module}.`
      : 'No tienes permisos para acceder a esta seccion.')

  const helpText = isUnconfigured
    ? 'Si necesitas acceso urgente, contacta al equipo de soporte.'
    : 'Contacta a tu administrador para solicitar los permisos necesarios.'

  return (
    <div className="flex items-center justify-center h-[calc(100vh-4rem)] px-4">
      <div className="max-w-sm w-full text-center">
        {/* Icon */}
        <div className="mx-auto w-14 h-14 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-5">
          <LockClosedIcon className="w-7 h-7 text-gray-400 dark:text-gray-500" />
        </div>

        {/* Title */}
        <h1 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {title}
        </h1>

        {/* Description */}
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
          {description}
        </p>

        {/* Help text */}
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-6">
          {helpText}
        </p>

        {/* Actions */}
        <div className="flex gap-3 justify-center">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Volver
          </button>

          {firstAccessibleHref && (
            <button
              onClick={() => navigate(firstAccessibleHref)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 transition-colors"
            >
              <HomeIcon className="h-4 w-4" />
              Ir al inicio
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
